import React, { Component } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import {
	BaseTabuRequest,
	ProcedureIDs,
	TabuDocumentRequest,
	TabuProcedure,
	ValidateBCSRequest,
	ValidatePropertyResponse,
	ValidateRequest,
} from "@10x/tabu-mobile-api/dist/requests/entity";
import { TabuRequestApi, TabuRequestStatusType, TabuRequestUtils } from "@10x/tabu-mobile-api";
import { I8N, LocaleUtil, StringUtil } from "@eres/core";
import { getParticipantID } from "@10x/sso";

import Strings from "@my-dld/assets/strings";
import { getParentFullPath } from "@my-dld/utils/PathUtils";
import { replaceNavigate } from "@my-dld/utils/StoreUtils";
import Dropzone from "react-dropzone";
import { getTabuDocDate, getTabuDocName, getTabuDocSize, getTabuDocType, TabuDocTypes } from "@my-dld/utils/TabuUtils";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { CommitteeDoc } from "../../ownercommitteeapi/entities";
import { AssetCard, TabuPropertyType } from "@10x/dubai-rest-api/dist/property_registry/entity";

export enum TabuLookupName {
	ISSUE_CERT_TYPES = "issueCertificateType",
	LOSS_CERT_TYPES = "lostCertificateTypes",
	EMIRATES = "emirates",
	LOSS_CERT_REQ_DOCUMENTS = "requiredDocuments",
	ZAJEL_AREAS = "zajelareas",
	VALUATION_TYPES = "evaluationtypes",
	LAND_VALUATION_TYPES = "landevaluationtypes",
	LAND_REQ_DOCUMENTS = "evaluationdocuments",
	MAP_EXCEPT_REASONS = "mapexceptionreasons",
}

export interface Lookup {
	Identity: number;
	NameAr: string;
	NameEn: string;
}

export interface ReqTabuDoc extends Lookup {
	MaxPages: number;
	MaxSize: number;
	IsRequired: boolean;
	docs?: TabuDoc[];
}

export interface TabuDoc {
	docName?: LocaleValue;
	identity: number;
	fileName?: string;
	fileSize?: number;
	localData?: string;
	type?: string;
	fileUrl?: string;
}

export interface BaseTabuRequestProps extends RoutedViewProps {

}

class BaseTabuRequestForm<Props, State> extends Component<Props, State> {

	protected navigateToVoucher = ( props: BaseTabuRequestProps, tabuProcedure: TabuProcedure ) => {
		const currentPath = getParentFullPath( props.location.pathname, "taburequest" );
		if ( tabuProcedure.Voucher && tabuProcedure.RequestStatusId != TabuRequestStatusType.UNDER_AUDIT ) {
			replaceNavigate( currentPath + "/voucher", undefined, tabuProcedure );
		} else {
			replaceNavigate( currentPath + "/complete", undefined, tabuProcedure );
		}
	};

	protected uploadDocuments( reqDocs: ReqTabuDoc[] ): Promise<TabuDocumentRequest[]> {
		let uploadedDoc: any[] = [];
		for ( const reqDoc of reqDocs ) {
			if ( reqDoc.docs && reqDoc.docs.length > 0 ) {
				for ( const tabuDoc of reqDoc.docs ) {
					if ( tabuDoc.localData ) {
						let extData = tabuDoc.localData.split( ";" )[ 0 ];
						let type = extData.includes( "image" ) ? "jpg" : "pdf";
						let encoded = tabuDoc.localData.replace( /^data:(.*;base64,)?/, "" );
						if ( ( encoded.length % 4 ) > 0 ) {
							encoded += "=".repeat( 4 - ( encoded.length % 4 ) );
						}
						uploadedDoc.push( TabuRequestApi.uploadTabuDocument( encoded, type ).then( ( response ) => {
								const remoteDoc: TabuDocumentRequest = {
									DocumentId: "" + reqDoc.Identity,
									DocumentPath: [ response.Response.FileName ]
								};
								return Promise.resolve( remoteDoc );
							} ).catch( ( reason ) => {
								return Promise.reject( reason );
							} )
						);
					} else if ( tabuDoc.fileUrl ) {
						let remoteDoc: TabuDocumentRequest = {
							DocumentId: "" + reqDoc.Identity,
							DocumentPath: [ tabuDoc.fileUrl ]
						};
						uploadedDoc.push( Promise.resolve( remoteDoc ) );
					}
				}
			}
		}
		return Promise.all( uploadedDoc );
	}
}

export function tabuServiceValidate( procedureId: number, propertyId: number, propertyTypeId: number,
                                     property: AssetCard, isMultiProperty: boolean ): Promise<boolean> {
	const request: ValidateRequest = {
		ProcedureId: procedureId,
		PropertyWithTypeIds: [ {
			PropertyId: propertyId,
			PropertyTypeId: propertyTypeId,
		} ],
		additionalData: [ {
			key: "ParticipantId",
			value: getParticipantID()!.toString(),
		}, ],
	};
	type X = ValidateRequest | ValidateBCSRequest;
	type VApi = ( validateReq: X ) => Promise<any>;
	let validateApi: VApi = TabuRequestApi.validateTabuService;
	if ( procedureId === ProcedureIDs.BCS_RE_CLASSIFICATION ) {
		validateApi = TabuRequestApi.validateBCSTabuService as VApi;
		const rq = request as ValidateBCSRequest;
		rq.PropertySource = "Tabu";
		rq.LandPropertyId = property.landPropertyId!;
		if ( property.propertyTypeId !== TabuPropertyType.LAND )
			rq.BuildingPropertyIds = [ property.buildingPropertyId! ];
		else rq.BuildingPropertyIds = [];
	}
	if ( isMultiProperty ) {
		validateApi = () => Promise.resolve( true );
	}
	return validateApi( request ).then( ( resp: ValidatePropertyResponse ) => {
		if ( resp && resp.Response && resp.Response.length > 0 ) {
			let messages = resp.Response.map( value => {
				return getTabuErrorMessage( value );
			} );
			let message = messages.join( "\n" );
			return Promise.reject( message );
		} else {
			if ( procedureId === ProcedureIDs.BCS_RE_CLASSIFICATION ) {
				return Promise.resolve( true );
			} else {
				return checkRequestAlreadyExist( request ).then( canProceed => {
					return Promise.resolve( canProceed );
				} ).catch( reason => {
					return Promise.reject( reason );
				} );
			}
		}
	} ).catch( ( reason ) => {
		console.error( reason.info ?? reason );
		return Promise.reject( reason );
	} );
}

function checkRequestAlreadyExist( request: ValidateRequest ): Promise<boolean> {
	const propertyId = request.PropertyWithTypeIds[ 0 ].PropertyId;
	const propertyTypeId = request.PropertyWithTypeIds[ 0 ].PropertyTypeId;
	return TabuRequestUtils.checkForExistingPendingProcedure( getParticipantID()!, request.ProcedureId, propertyId, propertyTypeId, Strings as any );
}

export function getTabuErrorMessage( serviceError: any ) {
	let message = undefined;
	if ( serviceError.ErrorMessage != undefined ) return serviceError.ErrorMessage;
	if ( serviceError.Name == undefined ) return serviceError.Value;
	message = LocaleUtil.getLocalValue( serviceError.Name );
	if ( message == undefined || message === "" )
		message = serviceError.Value;
	if ( message == undefined && serviceError.ErrorMessage != undefined ) {
		message = serviceError.ErrorMessage;
	}
	return message;
}

export function RenderTabuRequiredDocuments( props: {
	reqDocuments: ReqTabuDoc[], onDropRejected: () => void,
	onDrop: ( ( doc: ReqTabuDoc, files: File[] ) => void ), onDelete: ( identity: number, index: number ) => void
} ) {
	const { reqDocuments, onDropRejected, onDrop, onDelete } = props;
	return ( <div className="row">
		<div className={ "col-12 mb-2 content-section" }>
			<div className={ "content-section-title" }><span
				className="section-title-span">{ Strings.upload_documents }</span></div>
		</div>
		{ reqDocuments.map( ( reqTabuDoc: ReqTabuDoc, index: number ) => {
			let title = I8N.isRTL() ? reqTabuDoc.NameAr : reqTabuDoc.NameEn;
			const maxSize = reqTabuDoc.MaxSize || 2048;
			const maxSizeMB = ( maxSize / 1024 ).toFixed( 2 );
			return <div key={ "" + index } className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
				<Dropzone
					multiple={ reqTabuDoc.MaxPages > 1 }
					maxSize={ maxSize * 1000 }
					onDropRejected={ onDropRejected }
					onDrop={ ( files: File[] ) => {
						onDrop( reqTabuDoc, files );
					} }
					accept={ [ "image/png", "image/jpeg", "application/pdf" ] }>
					{ ( { getRootProps, getInputProps } ) => (
						<div className="form-group field-style" { ...getRootProps() }>
							<label>{ title }</label>{ reqTabuDoc.IsRequired && <span className="asterisk">*</span> }
							<div className="custom-file-upload">
								<input className="custom-file-input field-ar" { ...getInputProps() }/>
								<label
									className="custom-file-label">{ Strings.choose_files }</label>
								<i className="fas fa-upload"/>
							</div>
							<div><small>{ Strings.upload_documents_note.replace( "~", maxSizeMB ) }</small></div>
							<div><small>{ Strings.max_num_files + " : " + reqTabuDoc.MaxPages }</small></div>
						</div>
					) }
				</Dropzone>
				{ reqTabuDoc.docs && reqTabuDoc.docs.length > 0 &&
					<RenderTabuUploadDocument
						docs={ reqTabuDoc.docs }
						onDelete={ ( identity: number, index: number ) => {
							onDelete( identity, index );
						} }/> }
			</div>;
		} )
		}
	</div> );
}

export function RenderTabuUploadDocument( props: { docs: TabuDoc[], onDelete: ( identity: number, index: number ) => void } ) {
	const { docs, onDelete } = props;
	return <div className={ "mb-2" }>
		{ docs.map( ( tabuDoc: TabuDoc, index: number ) => {
			let docType = getTabuDocType( tabuDoc );
			let preview = ( docType === TabuDocTypes.PDF ) ?
				<i className="far fa-file-pdf"/> : getImagePreview( tabuDoc );
			return <table
				key={ "" + index }
				className="table table-bordered table-condensed table-striped table-responsive-sm upload-table">
				<thead>
				<tr>
					<th>{ Strings.file }</th>
					<th>{ Strings.name }</th>
					<th>{ Strings.size }</th>
					<th>{ Strings.vid_date }</th>
					<th>{ Strings.action }</th>
				</tr>
				</thead>
				<tbody>
				<tr>
					<td data-label={ Strings.file }>{ preview }</td>
					<td data-label={ Strings.name }>{ getTabuDocName( tabuDoc ) }</td>
					<td data-label={ Strings.size }>{ getTabuDocSize( tabuDoc ) + " " + Strings.size_mb }</td>
					<td data-label={ Strings.vid_date }>{ getTabuDocDate( tabuDoc ) }</td>
					<td data-label={ Strings.action }>
						<button
							className="btn btn-grey" onClick={ () => {
							onDelete( tabuDoc.identity, index );
						} }><i className="far fa-trash-alt"/>{ Strings.delete }
						</button>
					</td>
				</tr>
				</tbody>
			</table>;
		} )
		}
	</div>;
}

export function getImagePreview( tabuDoc: TabuDoc | CommitteeDoc ) {
	let source = ( tabuDoc.localData && !StringUtil.isEmptyStringOrNull( tabuDoc.localData ) ) ?
		tabuDoc.localData : tabuDoc.fileUrl;
	return <img src={ source } style={ { height: 40, width: 40 } } alt={ "uploaded_image" }/>;
}

export default BaseTabuRequestForm;

export function addAgentParticipantId( property: AssetCard | undefined, req: Partial<BaseTabuRequest> ) {
	if ( property?.isAgent ) {
		req.CompanyParticipantId = property?.agentCompany?.number + "";
	}
}
